import React, { useState, useEffect, useCallback } from "react";
import { EmotionChip } from "@/components/EmotionChip";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Film, ChevronRight, AlertCircle, ArrowLeft, Edit2, Zap, Star, Download, FileText, Loader2, ScrollText } from "lucide-react";
import TopNav from "../components/TopNav";
import ArcPlannerView from "./ArcPlannerPage";
import { StaleIndicator } from "../components/StaleIndicator";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Toaster, toast } from "../components/ui/sonner";
import { DemoProvider } from "../contexts/DemoContext";
import { DemoBadge } from "../components/DemoBadge";
import { DemoWalkthrough } from "../components/DemoWalkthrough";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CE_COLORS = {
  "CE-01": "var(--ce-01-romance)", "CE-02": "var(--ce-02-joy)", "CE-03": "var(--ce-03-sorrow)",
  "CE-04": "var(--ce-04-fury)", "CE-05": "var(--ce-08-valor)", "CE-06": "var(--ce-05-wonder)",
  "CE-07": "var(--ce-06-dread)", "CE-08": "var(--ce-07-revulsion)", "CE-09": "var(--ce-09-serenity)",
};
const CE_NAMES = {
  "CE-01": "Romance", "CE-02": "Joy", "CE-03": "Sorrow", "CE-04": "Fury",
  "CE-05": "Valor", "CE-06": "Dread", "CE-07": "Revulsion", "CE-08": "Wonder", "CE-09": "Serenity",
};

function ScoreChip({ score }) {
  if (!score && score !== 0) return null;
  const cls = score >= 75 ? "score-high" : score >= 50 ? "score-mid" : "score-low";
  return <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${cls}`}>{score}</span>;
}

function AddEpisodeModal({ projectId, onClose, onAdd }) {
  const [form, setForm] = useState({ title: "", episode_number: 1, primary_ce: "CE-01", emotional_intensity: 5 });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/episodes`, form, { withCredentials: true });
      onAdd(data);
      onClose();
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="relative w-full max-w-sm rounded-2xl p-6 z-10"
        style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}>
        <h3 className="font-bold text-base mb-4">Add Episode</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[var(--ink-secondary)] text-xs block mb-1">Episode Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
              placeholder="Episode title" data-testid="add-episode-title"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[var(--ink-secondary)] text-xs block mb-1">Episode #</label>
              <input type="number" value={form.episode_number} min={1}
                onChange={e => setForm({ ...form, episode_number: parseInt(e.target.value) || 1 })}
                data-testid="add-episode-number"
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }} />
            </div>
            <div>
              <label className="text-[var(--ink-secondary)] text-xs block mb-1">Intensity (1-10)</label>
              <input type="number" value={form.emotional_intensity} min={1} max={10}
                onChange={e => setForm({ ...form, emotional_intensity: parseInt(e.target.value) || 5 })}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }} />
            </div>
          </div>
          <div>
            <label className="text-[var(--ink-secondary)] text-xs block mb-1">Primary Emotion</label>
            <select value={form.primary_ce} onChange={e => setForm({ ...form, primary_ce: e.target.value })}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
              {Object.entries(CE_NAMES).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2 rounded-lg text-[var(--ink-secondary)] text-sm border" style={{ borderColor: "var(--border-subtle)" }}>
              Cancel
            </button>
            <button type="submit" disabled={loading} data-testid="add-episode-submit"
              className="flex-1 py-2 rounded-lg font-semibold text-sm"
              style={{ background: "var(--accent-gold)" }}>
              {loading ? "Adding..." : "Add Episode"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function AddSceneModal({ episode, onClose, onAdd }) {
  const [form, setForm] = useState({ location: "INT. LOCATION - DAY", brief: "", primary_ce: episode?.template_primary_ce || episode?.primary_ce || "CE-01", characters: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      primary_ce: episode?.template_primary_ce || episode?.primary_ce || "CE-01",
    }));
  }, [episode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const chars = form.characters.split(",").map(c => c.trim()).filter(Boolean);
      const { data } = await axios.post(`${API}/episodes/${episode.id}/scenes`,
        { ...form, characters: chars }, { withCredentials: true });
      onAdd(data);
      onClose();
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="relative w-full max-w-sm rounded-2xl p-6 z-10"
        style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}>
        <h3 className="font-bold text-base mb-4">Add Scene</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[var(--ink-secondary)] text-xs block mb-1">Location / Scene Heading</label>
            <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
              placeholder="INT. OFFICE - DAY" data-testid="add-scene-location"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }} />
          </div>
          <div>
            <label className="text-[var(--ink-secondary)] text-xs block mb-1">Scene Brief</label>
            <textarea value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })}
              rows={4} required data-testid="add-scene-brief"
              placeholder="Describe what happens in this scene: setting, characters present, key beat, emotional intent..."
              className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }} />
          </div>
          <div>
            <label className="text-[var(--ink-secondary)] text-xs block mb-1">Characters (comma-separated)</label>
            <input value={form.characters} onChange={e => setForm({ ...form, characters: e.target.value })}
              placeholder="ARIA, CEO MARCUS" data-testid="add-scene-characters"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }} />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2 rounded-lg text-[var(--ink-secondary)] text-sm border" style={{ borderColor: "var(--border-subtle)" }}>
              Cancel
            </button>
            <button type="submit" disabled={loading} data-testid="add-scene-submit"
              className="flex-1 py-2 rounded-lg font-semibold text-sm"
              style={{ background: "var(--accent-gold)" }}>
              {loading ? "Adding..." : "Add Scene"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function ExportMenu({ disabled, exporting, onExport, testIdPrefix, buttonLabel }) {
  const options = [
    { format: "docx", label: "Word Document (.docx)", icon: FileText },
    { format: "pdf", label: "Screenplay PDF (.pdf)", icon: Download },
    { format: "fountain", label: "Fountain (.fountain)", icon: ScrollText },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || exporting}
          aria-label={buttonLabel}
          data-testid={`${testIdPrefix}-trigger`}
          className="h-9 rounded-xl border-[var(--border-subtle)] bg-[var(--surface-card)] px-3 text-[var(--ink-primary)] hover:bg-[var(--surface-raised)] hover:"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {exporting ? "Exporting..." : buttonLabel}
          <ChevronRight className="h-4 w-4 rotate-90" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 border-[var(--border-subtle)] bg-[var(--surface-card)]"
        data-testid={`${testIdPrefix}-content`}
      >
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <DropdownMenuItem
              key={option.format}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onExport(option.format);
              }}
              data-testid={`${testIdPrefix}-${option.format}`}
              className="cursor-pointer rounded-lg px-3 py-2 text-sm text-[var(--ink-primary)] focus:bg-[var(--surface-raised)] focus:"
            >
              <Icon className="h-4 w-4 text-[var(--accent-gold)]" />
              {option.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function SeriesDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [view, setView] = useState("episodes");
  const [showAddEp, setShowAddEp] = useState(false);
  const [showAddScene, setShowAddScene] = useState(false);
  const [loading, setLoading] = useState(true);
  const [legacyOrphanCount, setLegacyOrphanCount] = useState(0);
  const [showOrphanBanner, setShowOrphanBanner] = useState(false);
  const [exportingKey, setExportingKey] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, epsRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`, { withCredentials: true }),
          axios.get(`${API}/projects/${projectId}/episodes`, { withCredentials: true }),
        ]);
        setProject(pRes.data);
        setEpisodes(epsRes.data);
        if (epsRes.data.length > 0) {
          const requestedEpisodeId = location.state?.selectedEpisodeId;
          const matchedEpisode = epsRes.data.find((ep) => ep.id === requestedEpisodeId) || epsRes.data[0];
          setSelectedEpisode(matchedEpisode);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [projectId, location.state]);

  useEffect(() => {
    if (!selectedEpisode) return;
    axios.get(`${API}/episodes/${selectedEpisode.id}/scenes`, { withCredentials: true })
      .then(r => {
        setScenes(r.data);
        const orphanCount = r.data.filter((scene) => scene.scene_number == null).length;
        setLegacyOrphanCount(orphanCount);
        setShowOrphanBanner(orphanCount > 0);
      })
      .catch(console.error);
  }, [selectedEpisode]);

  useEffect(() => {
    if (!selectedEpisode || !location.state?.openBriefEntry) return;
    if (location.state.selectedEpisodeId && location.state.selectedEpisodeId !== selectedEpisode.id) return;
    setShowAddScene(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [selectedEpisode, location.pathname, location.state, navigate]);

  if (loading) return (
    <div className="min-h-screen" style={{ background: "var(--surface-page)" }}>
      <TopNav />
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 rounded-full border-2 border-[var(--accent-gold)] border-t-transparent animate-spin" />
      </div>
    </div>
  );

  const ceColor = selectedEpisode ? (CE_COLORS[selectedEpisode.primary_ce] || "var(--accent-gold)") : "var(--accent-gold)";
  const visibleScenes = scenes.filter((scene) => scene.scene_number != null);

  const handleCleanupOrphans = async () => {
    if (!selectedEpisode) return;
    try {
      await axios.post(`${API}/episodes/${selectedEpisode.id}/cleanup-orphans`, {}, { withCredentials: true });
      const refreshed = await axios.get(`${API}/episodes/${selectedEpisode.id}/scenes`, { withCredentials: true });
      setScenes(refreshed.data);
      setLegacyOrphanCount(0);
      setShowOrphanBanner(false);
    } catch (err) {
      console.error(err);
    }
  };

  const parseExportError = async (error) => {
    const payload = error?.response?.data;
    if (payload instanceof Blob) {
      try {
        const text = await payload.text();
        const json = JSON.parse(text);
        return json.detail || "Export failed";
      } catch {
        return "Export failed";
      }
    }
    return payload?.detail || error?.message || "Export failed";
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const deriveFilename = (headers, fallback) => {
    const disposition = headers?.["content-disposition"] || headers?.get?.("content-disposition");
    const match = disposition?.match(/filename=([^;]+)/i);
    return match ? match[1].replace(/"/g, "") : fallback;
  };

  const handleExport = async ({ level, targetId, format }) => {
    const key = `${level}-${targetId}-${format}`;
    setExportingKey(key);
    try {
      const response = await axios.post(`${API}/export/${level}/${targetId}?format=${format}`, null, {
        withCredentials: true,
        responseType: "blob",
      });
      const extension = format === "fountain" ? "fountain" : format;
      triggerDownload(response.data, deriveFilename(response.headers, `${level}_${targetId}.${extension}`));
    } catch (error) {
      toast.error(await parseExportError(error), { id: `${level}-export-error-toast` });
    } finally {
      setExportingKey("");
    }
  };

  return (
    <DemoProvider project={project}>
    <div className="min-h-screen flex flex-col" style={{ background: "var(--surface-page)" }}>
      {project?.is_demo && <DemoWalkthrough />}
      <TopNav />
      <Toaster richColors />
      <div className="flex flex-1 overflow-hidden" style={{ height: "calc(100vh - 56px)" }}>
        {/* Left sidebar */}
        <div className="w-64 flex-shrink-0 flex flex-col border-r overflow-hidden" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="p-4 border-b" style={{ borderColor: "var(--border-subtle)" }}>
            <button onClick={() => navigate("/studio")}
              className="flex items-center gap-1.5 text-[var(--ink-tertiary)] hover:text-[var(--ink-secondary)] text-xs mb-3 transition-colors">
              <ArrowLeft size={12} />
              Back to Studio
            </button>
            <h2 className="font-bold text-sm truncate">{project?.title}</h2>
            {project?.is_demo && (
              <div className="mt-1">
                <DemoBadge variant="compact" />
              </div>
            )}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[var(--ink-tertiary)] text-xs capitalize">{project?.genre}</span>
              <span className="text-[var(--border-subtle)]">·</span>
              <span className="text-[var(--ink-tertiary)] text-xs capitalize">{project?.platform_target}</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex p-2 gap-1 border-b" style={{ borderColor: "var(--border-subtle)" }}>
            {["episodes", ...(project?.is_demo ? [] : ["arc"])].map(v => (
              <button key={v} onClick={() => setView(v)}
                data-testid={`view-tab-${v}`}
                className="flex-1 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all"
                style={view === v
                  ? { background: "rgba(176,141,87,0.18)", color: "var(--ink-primary)", borderBottom: "2px solid var(--accent-gold)" }
                  : { color: "var(--ink-secondary)" }}>
                {v === "episodes" ? "Episodes" : "Arc Planner"}
              </button>
            ))}
          </div>

          {/* Episode list */}
          <div className="flex-1 overflow-y-auto">
            {episodes.map((ep) => {
              const epColor = CE_COLORS[ep.primary_ce] || "var(--accent-gold)";
              return (
                <div key={ep.id}
                  onClick={() => { setSelectedEpisode(ep); setView("episodes"); }}
                  data-testid={`episode-item-${ep.id}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && setSelectedEpisode(ep)}
                  className="w-full text-left px-4 py-3 border-b transition-colors flex items-start gap-3 cursor-pointer"
                  style={{
                    borderColor: "var(--surface-card)",
                    background: selectedEpisode?.id === ep.id ? "var(--surface-card)" : "transparent",
                  }}>
                  <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: epColor }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[var(--ink-tertiary)] text-xs">{ep.episode_number}</span>
                      <span className="text-xs font-medium truncate">{ep.title}</span>
                    </div>
                    <div className="text-[var(--ink-tertiary)] text-xs mt-0.5 flex items-center gap-1">
                      <EmotionChip ceCode={ep.primary_ce} size="sm" />
                      {ep.is_paywall_gate && <span className="text-[var(--semantic-warning)]">💰</span>}
                      <span className="ml-auto">{ep.scene_count || 0} scenes</span>
                    </div>
                  </div>
                  <div onClick={(event) => event.stopPropagation()} className="ml-2 flex-shrink-0">
                    <ExportMenu
                      disabled={false}
                      exporting={exportingKey.startsWith(`episode-${ep.id}-`)}
                      onExport={(format) => handleExport({ level: "episode", targetId: ep.id, format })}
                      testIdPrefix={`episode-export-menu-${ep.id}`}
                      buttonLabel="Export"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-3 border-t" style={{ borderColor: "var(--border-subtle)" }}>
            {!project?.is_demo && (
            <button onClick={() => setShowAddEp(true)} data-testid="add-episode-button"
              className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-colors"
              style={{ background: "var(--surface-card)", color: "var(--ink-secondary)", border: "1px dashed var(--border-subtle)" }}>
              <Plus size={14} />
              Add Episode
            </button>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto p-6">
          {view === "arc" ? (
            <ArcPlannerView projectId={projectId} episodes={episodes} />
          ) : selectedEpisode ? (
            <div>
              {showOrphanBanner && legacyOrphanCount > 0 && (
                <div
                  className="mb-4 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  data-testid="legacy-orphan-banner"
                  style={{ background: "rgba(198,166,100,0.1)", border: "1px solid rgba(198,166,100,0.3)" }}
                >
                  <div className="flex items-start gap-2 text-sm text-[var(--accent-gold)]">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>We detected {legacyOrphanCount} placeholder scenes from a prior template. Clean up?</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowOrphanBanner(false)}
                      data-testid="dismiss-orphan-banner"
                      className="px-3 py-1.5 rounded-lg text-xs text-[var(--accent-gold)]"
                      style={{ background: "var(--surface-page)", border: "1px solid rgba(198,166,100,0.3)" }}
                    >
                      Dismiss
                    </button>
                    <button
                      onClick={handleCleanupOrphans}
                      data-testid="cleanup-orphan-scenes-button"
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                      style={{ background: "var(--accent-gold)" }}
                    >
                      Clean Up
                    </button>
                  </div>
                </div>
              )}

              {/* Episode header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ background: ceColor }} />
                    <h2 className="font-bold text-xl">
                      Ep {selectedEpisode.episode_number}: {selectedEpisode.title}
                    </h2>
                    {selectedEpisode.is_paywall_gate && (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(198,166,100,0.1)", color: "var(--ce-02-joy)", border: "1px solid rgba(198,166,100,0.3)" }}>
                        Paywall Gate
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 ml-6">
                    <EmotionChip ceCode={selectedEpisode.primary_ce} size="md" />
                    <span className="text-[var(--ink-tertiary)] text-sm">· Intensity {selectedEpisode.emotional_intensity}/10</span>
                  </div>
                </div>
                <ExportMenu
                  disabled={!visibleScenes.some((scene) => scene.generated_script)}
                  exporting={exportingKey.startsWith(`project-${projectId}-`)}
                  onExport={(format) => handleExport({ level: "project", targetId: projectId, format })}
                  testIdPrefix="project-export-menu"
                  buttonLabel="Export All"
                />
              </div>

              {/* Scenes */}
              <div className="space-y-3">
                <AnimatePresence>
                  {visibleScenes.map((scene, i) => (
                    <motion.div key={scene.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="arc-card rounded-xl p-4 cursor-pointer group"
                      style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
                      onClick={() => navigate(`/studio/${projectId}/episodes/${selectedEpisode.id}/scenes/${scene.id}`)}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[var(--ink-tertiary)] text-xs font-mono">Scene {scene.scene_number}</span>
                            <span className="text-[var(--border-subtle)]">·</span>
                            <span className="text-[var(--ink-secondary)] text-xs truncate">{scene.location}</span>
                            <StaleIndicator
                              stale={scene.stale}
                              size="sm"
                              data-testid={`series-detail-scene-stale-${scene.id}`}
                            />
                          </div>
                          {(scene.brief || scene.context) && (
                            <p className="text-sm leading-relaxed line-clamp-2 mb-2">{scene.brief || scene.context}</p>
                          )}
                          <div className="flex items-center gap-2 flex-wrap">
                            {scene.primary_ce && (
                              <div className="flex items-center gap-[6px]">
                                <EmotionChip ceCode={scene.primary_ce} size="md" />
                                {scene.secondary_ce && <EmotionChip ceCode={scene.secondary_ce} size="md" />}
                              </div>
                            )}
                            {(scene.selected_ts || []).slice(0, 2).map(ts => (
                              <span key={ts} className="text-xs px-2 py-0.5 rounded-full text-[var(--ink-tertiary)]"
                                style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}>{ts}</span>
                            ))}
                          </div>
                          {scene.generated_script && (
                            <p className="text-[var(--ink-tertiary)] text-xs mt-2 line-clamp-1 screenplay-font">
                              {scene.generated_script.slice(0, 120)}...
                            </p>
                          )}
                          {scene.scene_number === 6 && !scene.generated_script && project?.is_demo && (
                            <span className="mt-2 block text-xs font-semibold" style={{ color: "var(--accent-gold)" }}>
                              ✦ Your turn — generate this scene live
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                          <ScoreChip score={scene.emotional_score} />
                          <ChevronRight size={16} className="text-[var(--border-subtle)] group-hover:text-[var(--accent-gold)] transition-colors" />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                <button onClick={() => setShowAddScene(true)} data-testid="add-scene-button"
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl text-sm transition-colors"
                  style={{ background: "transparent", border: "1px dashed var(--border-subtle)", color: "var(--ink-tertiary)" }}>
                  <Plus size={15} />
                  Add Scene
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <Film size={48} className="text-[var(--border-subtle)] mb-4" />
              <p className="text-[var(--ink-tertiary)]">No episodes yet. Add your first episode to start.</p>
            </div>
          )}
        </div>
      </div>

      {showAddEp && (
        <AddEpisodeModal
          projectId={projectId}
          onClose={() => setShowAddEp(false)}
          onAdd={(ep) => { setEpisodes([...episodes, ep]); setSelectedEpisode(ep); }}
        />
      )}
      {showAddScene && selectedEpisode && (
        <AddSceneModal
          episode={selectedEpisode}
          onClose={() => setShowAddScene(false)}
          onAdd={(sc) => setScenes([...scenes, sc])}
        />
      )}
    </div>
    </DemoProvider>
  );
}
